import { memo, useCallback, useEffect, useRef, useState } from "react";

import { useTranslate } from "helpers";
import { useHandleTextFieldChange, useHandleCheckboxChange } from "services/forms/hooks";
import { Errors } from "services/forms/validation/types";
import { validateYupWithFeedback } from "services/forms/validation/feedback";

import { Button } from "ui/components/Button/Button";
import { TextField } from "ui/components/TextField/TextField";
import TextArea from "ui/components/TextField/TextArea/TextArea";
import { CheckBox } from "ui/components/CheckBox";
import { Modal } from "ui/components/Modal";
import ModalHeader from "ui/components/Modal/ModalHeader";
import ModalBody from "ui/components/Modal/ModalBody";
import ModalFooter from "ui/components/Modal/ModalFooter";

import {
  createListingTemplateAPI,
  getListingTemplateAPI,
  updateListingTemplateAPI,
} from "services/api/settings/listings/api";
import {
  LISTING_TEMPLATE_VARIABLES,
  initialListingTemplateForm,
} from "services/api/settings/listings/consts";
import { ListingTemplateFormValues } from "services/api/settings/listings/types";

import { ListingTemplateEditCardProps } from "./types";
import { LISTING_TEMPLATE_EDIT_FORM_SCHEMA } from "./validation";

const ListingTemplateEditCard: React.FC<ListingTemplateEditCardProps> = (props) => {
  const { show, id, onSuccess, onClose } = props;

  const { t } = useTranslate();

  const [formValues, setFormValues] = useState<ListingTemplateFormValues>(initialListingTemplateForm);
  const [errors, setErrors] = useState<Errors>({});
  const [isLoading, setIsLoading] = useState<boolean>(false);

  // Zadnje fokusirano polje — varijabla se umeće u njega, a ne uvijek u opis.
  const [activeField, setActiveField] = useState<"titleTemplate" | "descriptionTemplate">("descriptionTemplate");
  const titleRef = useRef<HTMLInputElement>(null);
  const descriptionRef = useRef<HTMLTextAreaElement>(null);

  const handleTextChange = useHandleTextFieldChange(setFormValues, formValues);
  const handleCheckboxChange = useHandleCheckboxChange(setFormValues);

  const fetchTemplate = useCallback(async (templateId: string) => {
    setIsLoading(true);
    try {
      const result = await getListingTemplateAPI(templateId);
      setFormValues({
        name: result.name,
        titleTemplate: result.titleTemplate,
        descriptionTemplate: result.descriptionTemplate,
        isDefault: result.isDefault,
      });
    } catch {
      // greške rukuje createApiCall (snackbar)
    }
    setIsLoading(false);
  }, []);

  useEffect(() => {
    if (!show) return;

    setErrors({});

    if (id) fetchTemplate(id);
    else setFormValues(initialListingTemplateForm);
  }, [show, id, fetchTemplate]);

  const handleTitleFocus = () => setActiveField("titleTemplate");
  const handleDescriptionFocus = () => setActiveField("descriptionTemplate");

  /** Umeće varijablu na poziciju kursora u zadnje aktivno polje. */
  const handleVariableClick = (code: string) => {
    const token = `{${code}}`;
    const element = activeField === "titleTemplate" ? titleRef.current : descriptionRef.current;
    const current = formValues[activeField];

    const start = element?.selectionStart ?? current.length;
    const end = element?.selectionEnd ?? current.length;
    const next = `${current.slice(0, start)}${token}${current.slice(end)}`;

    setFormValues((prev) => ({ ...prev, [activeField]: next }));

    // Kursor iza umetnute varijable, da se može nastaviti tipkati.
    window.setTimeout(() => {
      element?.focus();
      element?.setSelectionRange(start + token.length, start + token.length);
    }, 0);
  };

  const handleSave = async () => {
    if (!validateYupWithFeedback(formValues, LISTING_TEMPLATE_EDIT_FORM_SCHEMA, setErrors)) return;

    setIsLoading(true);
    try {
      if (id) await updateListingTemplateAPI(id, formValues, t);
      else await createListingTemplateAPI(formValues, t);

      onSuccess();
    } catch {
      // greške rukuje createApiCall (snackbar)
    }
    setIsLoading(false);
  };

  return (
    <Modal show={show} onHide={onClose} size="medium">
      <ModalHeader
        icon="/media/images/add-icon.svg"
        title={id ? "Uredi predložak oglasa" : "Novi predložak oglasa"}
        onHide={onClose}
      />
      <ModalBody fixedFooterSpacing={false}>
        <div className="p-4 flex flex-col gap-4">
          {/* Naslov i opis kao na vatrogasnim aparatima — centrirano iznad forme. */}
          <div>
            <h1 className="text-2xl font-bold text-slate-900 text-center mb-1">Predložak oglasa</h1>
            <p className="text-center text-md text-slate-600 mb-4 max-w-2xl mx-auto">
              Napišite naslov i opis koji se primjenjuju na novi oglas. U tekst ubacite varijable
              poput <strong>{"{marka}"}</strong> ili <strong>{"{model}"}</strong> — pri kreiranju
              oglasa zamijenit će se podacima vozila.
            </p>
          </div>

          <div>
            <TextField
              type="text"
              name="name"
              label="Naziv predloška"
              placeholder="npr. Rabljena vozila"
              value={formValues.name}
              onChange={handleTextChange}
              error={errors.name}
              required
            />
          </div>

          {/* onFocusCapture umjesto propa na polju — TextField ga ne prosljeđuje. */}
          <div onFocusCapture={handleTitleFocus}>
            <TextField
              ref={titleRef}
              type="text"
              name="titleTemplate"
              label="Naslov oglasa"
              placeholder="{marka} {model} {tip}"
              value={formValues.titleTemplate}
              onChange={handleTextChange}
              error={errors.titleTemplate}
              required
              disableDebounce
            />
          </div>

          <div onFocusCapture={handleDescriptionFocus}>
            <TextArea
              ref={descriptionRef}
              name="descriptionTemplate"
              label="Opis oglasa"
              placeholder="Prodaje se {marka} {model}, godina {godina}, prijeđeno {kilometraza}."
              value={formValues.descriptionTemplate}
              onChange={handleTextChange}
              rows={40}
              disableDebounce
            />
          </div>

          {/* Klik umeće varijablu u polje na kojem je kursor. */}
          <div>
            <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold mb-2">
              Varijable
            </div>
            <div className="flex flex-wrap gap-2">
              {LISTING_TEMPLATE_VARIABLES.map((variable) => (
                <button
                  key={variable.code}
                  type="button"
                  onClick={() => handleVariableClick(variable.code)}
                  title={variable.label}
                  className="px-3 py-2.5 rounded-sm border border-slate-200 bg-white text-slate-600 text-sm hover:border-sky-300 hover:text-sky-700"
                >
                  {`{${variable.code}}`}
                </button>
              ))}
            </div>
            <div className="text-xs text-slate-400 mt-2">
              Pri kreiranju oglasa varijable se zamjenjuju podacima vozila.
            </div>
          </div>

          <div>
            <CheckBox
              name="isDefault"
              checked={formValues.isDefault}
              onChange={handleCheckboxChange}
              label="Zadani predložak (automatski se primjenjuje na novi oglas)"
            />
          </div>
        </div>
      </ModalBody>
      <ModalFooter>
        <div className="flex justify-between gap-3 px-4 py-3">
          <Button variant="outlined" children={t("admin.close")} onClick={onClose} disabled={isLoading} />
          <Button variant="primary" children={t("admin.save")} onClick={handleSave} loading={isLoading} />
        </div>
      </ModalFooter>
    </Modal>
  );
};

export default memo(ListingTemplateEditCard);
